import type { AuthResponse, PublicUser } from "@adafia/types";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { fetchWithAuth, getApiBase } from "./api";

interface AuthContextType {
	isAuthenticated: boolean;
	user: PublicUser | null;
	login: (email: string, password: string) => Promise<boolean>;
	logout: () => void;
	isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const [user, setUser] = useState<PublicUser | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const verifyToken = async () => {
			// Check URL for token from OAuth redirect
			const urlParams = new URLSearchParams(window.location.search);
			const tokenFromUrl = urlParams.get("token");
			if (tokenFromUrl) {
				localStorage.setItem("adafia_auth_token", tokenFromUrl);
				// Clean up URL without triggering a page reload
				window.history.replaceState(
					{},
					document.title,
					window.location.pathname,
				);
			}

			try {
				const response = await fetchWithAuth(
					`${getApiBase()}/api/v1/auth/verify`,
				);

				if (response.ok) {
					const data = await response.json();
					setUser(data.user);
				} else {
					setUser(null);
				}
			} catch (err) {
				console.error("Token verification error:", err);
				setUser(null);
			} finally {
				setIsLoading(false);
			}
		};

		verifyToken();
	}, []);

	const login = async (email: string, password: string): Promise<boolean> => {
		try {
			const response = await fetch(`${getApiBase()}/api/v1/auth/login`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ email, password }),
				credentials: "include",
			});

			if (response.ok) {
				const data: AuthResponse = await response.json();
				localStorage.setItem("adafia_auth_token", data.token);
				setUser(data.user);
				return true;
			}
			return false;
		} catch (err) {
			console.error("Login error:", err);
			return false;
		}
	};
	const logout = () => {
		localStorage.removeItem("adafia_auth_token");
		// Note: We might also want to clear the cookie here if we had a logout endpoint
		setUser(null);
	};

	return (
		<AuthContext.Provider
			value={{ isAuthenticated: !!user, user, login, logout, isLoading }}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
